import type { CollectionResponseType } from '@app/src/api/fetchHooks'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import endpoints from '@app/src/api/endpoints'
import { ONE_MINUTE_MS } from '@app/src/wf-constants'
import { SortOrder } from '@app/src/types/filter'
import { useMemo } from 'react'
import type { QuestionCategory, QuestionCategoryType } from '@app/src/types/resourceExplorer'

export const categoryTypeOrder: QuestionCategoryType[] = ['Basic', 'Regular', 'ESRS']

const ESG_AREAS_IN_ORDER = ['environment', 'social', 'governance', 'other']

const sortEsgAreasInOrder = (a: string, b: string) =>
  ESG_AREAS_IN_ORDER.indexOf(a?.toLowerCase()) - ESG_AREAS_IN_ORDER.indexOf(b?.toLowerCase())

export const useQuestionCategories = () => {
  const {
    items: categories = [],
    isLoading,
    error,
  } = useFetchCollectionWithPost<QuestionCategory>({
    key: [FetchKey.QuestionCategories],
    endpoint: endpoints.questionCategoryCollection,
    payload: {
      filter: [],
      include: [],
      sort: { target: 'name', order: SortOrder.ASCENDING },
    },
    options: {
      staleTime: ONE_MINUTE_MS,
      select: (data: CollectionResponseType<QuestionCategory>): CollectionResponseType<QuestionCategory> => ({
        ...data,
        items: data.items.map(category => ({
          ...category,
          customSort: category.name === 'ESG' ? sortEsgAreasInOrder : undefined,
        })),
      }),
    },
  })

  const categoriesByType = useMemo(
    () => Object.groupBy(categories, c => c.categoryType as QuestionCategoryType),
    [categories],
  )
  const basicCategories = categoriesByType['Basic'] || []
  const regularCategories = categoriesByType['Regular'] || []
  const esrsCategories = categoriesByType['ESRS'] || []
  const basicAndRegularCategories = useMemo(
    () => [...basicCategories, ...regularCategories],
    [basicCategories, regularCategories],
  )
  const options = useMemo(
    () =>
      categories.map(category => ({
        label: category.name,
        value: category.id,
        additionalText: category.categoryType,
      })),
    [categories],
  )

  return {
    isLoading,
    error,
    categories,
    basicCategories,
    regularCategories,
    esrsCategories,
    basicAndRegularCategories,
    options,
  }
}
